/**
 * P2-WORKSPACE — the designer workspace (vertical slice UI).
 *
 * Wires the headless design-session contracts into React:
 *   retrieve (engine on the selected customer profile) → adjust (override
 *   fields w/ required reason) → render (SVG from RenderFacet + numeric
 *   readouts from the SAME state) → version (append-only log view) →
 *   approve (gated on blockers; actor = the signed-in designer).
 *
 * Loading/stale/error/no-match states (P2 deliverable 8): library build
 * failure = error state; empty retrieval = no-match state with defer
 * messaging; every mutation is synchronous (no stale window).
 */
import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Chip, Divider, Grid, Paper,
  Stack, TextField, Typography, MenuItem,
} from '@mui/material';
import { adjustDesign, approveSession, startDesignSession, type DesignSessionView } from '../contracts/designSession';
import { buildReferenceLibrary, normalizeReferenceDesigns } from '../contracts/referenceLibrary';
import {
  buildInterviewPayload, interviewHasSignal, loadingInterview, readyInterview,
  errorInterview, noMatchInterview, type InterviewState, type InterviewPayload,
  type InterviewDnaSummary,
} from '../contracts/interviewLoader';
import { stageApply, type ResearchFinding, type ResearchField } from '../contracts/researchPanel';
import { buildRenderFacetFromWire, svgWrapper, type WireArc } from '../contracts/renderFacet';
import type { OverrideField } from '../contracts/designVersioning';
import type { SkiType } from '../contracts/schemas';
import shortlistJson from '../../docs/contracts/analysis/gold-standard-shortlist.json';

/** Minimal app-level API surface (auth provided by the app shell). */
export type ApiFn = (path: string, token: string, init?: RequestInit) => Promise<unknown>;


const shortlist = shortlistJson as unknown as {
  wagnerSamples: Array<Record<string, unknown>>;
};

const SKI_TYPES: SkiType[] = ['All-Mountain', 'Powder', 'Carving', 'Touring', 'Off-piste', '50-50'];

const OVERRIDABLE: Array<{ field: OverrideField; label: string; step: number }> = [
  { field: 'length', label: 'Length (cm)', step: 1 },
  { field: 'noseWidth', label: 'Nose width (mm)', step: 0.1 },
  { field: 'waistWidthRecommendation', label: 'Waist width (mm)', step: 0.1 },
  { field: 'tailWidth', label: 'Tail width (mm)', step: 0.1 },
];

export function DesignWorkspace({
  designerEmail,
  api,
  token,
}: {
  designerEmail: string;
  /** App-level authenticated fetch (Google JWT); optional for tests. */
  api?: ApiFn;
  token?: string;
}) {
  const [skiType, setSkiType] = useState<SkiType>('All-Mountain');
  const [lengthCm, setLengthCm] = useState('178');
  const [session, setSession] = useState<DesignSessionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [interview, setInterview] = useState<InterviewState>({ status: 'idle' });
  const [customerId, setCustomerId] = useState('');
  const [researchFinding, setResearchFinding] = useState<ResearchFinding | null>(null);
  const [researchQuery, setResearchQuery] = useState('');
  const [dxfOrderId, setDxfOrderId] = useState('');

  const lib = useMemo(() => {
    try {
      return buildReferenceLibrary(normalizeReferenceDesigns(shortlist).records, []);
    } catch (e) {
      setError(`Reference library failed to build: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
  }, []);

  const retrieve = () => {
    setError(null);
    if (!lib) { setError('Reference library unavailable — cannot retrieve.'); return; }
    const len = Number(lengthCm);
    if (!Number.isFinite(len) || len < 120 || len > 220) {
      setError('Length must be between 120 and 220 cm.');
      return;
    }
    try {
      setSession(startDesignSession({ profile: { skiType, lengthCm: len }, lib }));
      setEditValues({});
      setReason('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const applyEdit = (field: OverrideField, rawValue: string) => {
    if (!session) return;
    setError(null);
    const num = Number(rawValue);
    if (!Number.isFinite(num)) { setError(`${field}: not a number`); return; }
    if (!reason.trim()) { setError('An override reason is required (provenance is a product feature).'); return; }
    try {
      setSession(adjustDesign(session, {
        field,
        previousValue: session.state[field] as number,
        newValue: num,
        reason,
        by: designerEmail,
        at: new Date().toISOString(),
      }, lib!));
      setEditValues((v) => ({ ...v, [field]: '' }));
      setReason('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const approve = () => {
    if (!session) return;
    setError(null);
    try {
      setSession(approveSession(session, designerEmail, new Date().toISOString()));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // ---------------- live interview path (deliverable 8 in the UI) ----------------
  const loadInterview = async () => {
    if (!api || !token) { setError('Live interview path requires sign-in.'); return; }
    const id = customerId.trim();
    if (!/^[0-9]+$/.test(id)) { setError('Customer ID must be numeric.'); return; }
    setInterview(loadingInterview());
    setError(null);
    try {
      const customerRes = await api(`/api/v3/fjell/customer/${id}`, token) as Record<string, unknown>;
      const customer = (customerRes as { item?: Record<string, unknown> }).item ?? customerRes;
      if (!customer || customer.id === undefined) { setInterview(errorInterview('Customer not found.')); return; }
      const ordersRes = await api('/api/v3/fjell/order?limit=1000&offset=0', token) as unknown;
      const allOrders: Array<Record<string, unknown>> = Array.isArray(ordersRes)
        ? ordersRes
        : ((ordersRes as { items?: unknown[] }).items ?? (ordersRes as { data?: unknown[] }).data ?? []);
      const cid = Number(id);
      const orders = allOrders.filter((o) => Number(o.skierId) === cid || Number(o.buyerId) === cid);
      const orderForms: Record<number, Record<string, unknown>> = {};
      const salesAnswers: Record<number, Array<Record<string, unknown>>> = {};
      for (const o of orders) {
        const oid = Number(o.id);
        try {
          const formsRes = await api(`/api/v3/fjell/order/${oid}/orderForm?limit=1&offset=0`, token);
          const forms: Array<Record<string, unknown>> = Array.isArray(formsRes)
            ? formsRes
            : ((formsRes as { items?: unknown[] }).items ?? []);
          if (forms[0]) orderForms[oid] = forms[0];
        } catch { /* orderForm missing for this order — recorded as no form */ }
        try {
          const ansRes = await api(`/api/v3/fjell/order/${oid}/salesQuestionAnswer?limit=100&offset=0`, token);
          const answers: Array<Record<string, unknown>> = Array.isArray(ansRes)
            ? ansRes
            : ((ansRes as { items?: unknown[] }).items ?? []);
          if (answers.length > 0) salesAnswers[oid] = answers;
        } catch { /* no answers for this order — fine */ }
      }
      const payload = buildInterviewPayload({
        nowMs: Date.now(), customer, orders, orderForms, salesAnswers,
      });
      const st = readyInterview(payload, Date.now());
      setInterview(st.payload || st.status === 'stale' ? st : noMatchInterview());
      if (!interviewHasSignal(payload)) setInterview(noMatchInterview());
    } catch (e) {
      setInterview(errorInterview(e instanceof Error ? e.message : String(e)));
    }
  };

  /** Seed the design session from the loaded interview (designer act). */
  const seedFromInterview = () => {
    if (interview.status !== 'ready' && interview.status !== 'stale') return;
    const payload = interview.payload as InterviewPayload;
    if (!lib) { setError('Reference library unavailable.'); return; }
    const orderWithLength = payload.orders.find((o) => o.lengthCm !== null);
    const lengthCmSeed = orderWithLength?.lengthCm ?? 170;
    try {
      setSession(startDesignSession({ profile: { skiType, lengthCm: lengthCmSeed }, lib }));
      setEditValues({});
      setReason('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // ---------------- research panel (deliverable 7 in the UI) ----------------
  const lookupResearch = () => {
    setError(null);
    const q = researchQuery.trim();
    if (!q) { setError('Enter a research query (e.g. skier name + model year).'); return; }
    // v0.1: deterministic local research simulation with explicit provenance —
    // real external research sources are a later phase (never silent about it).
    const finding: ResearchFinding = {
      researchKey: `local:${q}`,
      query: q,
      outcome: 'needs-verification',
      suggestions: {},
      provenance: {},
      rationale: `Local placeholder research for "${q}" — no external source wired yet; verify manually before applying.`,
    };
    setResearchFinding(finding);
  };

  const applyResearchField = (field: ResearchField) => {
    if (!session || !researchFinding) return;
    setError(null);
    try {
      const override = stageApply({
        finding: researchFinding,
        field,
        currentValue: session.state[field as keyof typeof session.state] as number | string,
        by: designerEmail,
        at: new Date().toISOString(),
      });
      setSession(adjustDesign(session, {
        field: field as OverrideField,
        previousValue: override.previousValue,
        newValue: override.newValue,
        reason: override.reason,
        by: override.by,
        at: override.at,
      }, lib!));
      setReason('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // ---------------- dxfSki fetch (real production outline) ----------------
  const [dxfArcs, setDxfArcs] = useState<WireArc[] | null>(null);
  const loadDxfSki = async (orderId: number) => {
    if (!api || !token) return;
    setError(null);
    try {
      const formsRes = await api(`/api/v3/fjell/order/${orderId}/orderForm?limit=1&offset=0`, token);
      const forms: Array<Record<string, unknown>> = Array.isArray(formsRes)
        ? formsRes
        : ((formsRes as { items?: unknown[] }).items ?? []);
      const formId = forms[0]?.id;
      if (!formId) { setDxfArcs(null); return; }
      const dxf = await api(`/api/v3/fjell/order/${orderId}/orderForm/${formId}/dxfSki`, token) as { arcs1?: WireArc[] };
      setDxfArcs(dxf.arcs1 ?? null);
    } catch (e) {
      setDxfArcs(null);
      setError(`dxfSki unavailable for order ${orderId}: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  if (!session) {
    return (
      <Card>
        <CardHeader title="New design session" subheader="Retrieve a recommendation from the gold-standard library, then adjust with provenance." />
        <CardContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              select label="Ski type" value={skiType} sx={{ minWidth: 180 }}
              onChange={(e) => setSkiType(e.target.value as SkiType)}
            >
              {SKI_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField
              label="Length (cm)" value={lengthCm}
              onChange={(e) => setLengthCm(e.target.value)} sx={{ width: 140 }}
            />
            <Button variant="contained" onClick={retrieve}>Retrieve recommendation</Button>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Live interview path</Typography>
          {api && token ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                label="Customer ID" size="small" sx={{ width: 160 }}
                value={customerId} onChange={(e) => setCustomerId(e.target.value)}
                placeholder="e.g. 335788"
              />
              <Button variant="outlined" onClick={loadInterview}>Load interview</Button>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Sign in to load a real customer interview (orders + SkierDNA).
            </Typography>
          )}
          {interview.status === 'loading' && (
            <Alert severity="info" sx={{ mt: 1 }}>Loading interview…</Alert>
          )}
          {interview.status === 'error' && (
            <Alert severity="error" sx={{ mt: 1 }}>Interview error: {interview.errorMessage}</Alert>
          )}
          {interview.status === 'no-match' && (
            <Alert severity="warning" sx={{ mt: 1 }}>No orders or SkierDNA records for this customer.</Alert>
          )}
          {interview.status === 'stale' && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Interview data is STALE ({Math.round((interview.staleForMs ?? 0) / 60000)} min old) — usable, but refresh if the conversation has moved on.
            </Alert>
          )}
          {(interview.status === 'ready' || interview.status === 'stale') && interview.payload && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                <b>{interview.payload.customer.name}</b>
                {' · '}{interview.payload.orders.length} order(s)
                {' · '}{interview.payload.dna.length} with DNA answers
              </Typography>
              {interview.payload.dna.map((d: InterviewDnaSummary) => (
                <Typography key={d.orderId} variant="caption" display="block" color="text.secondary">
                  order {d.orderId}: {d.answerCount} answers — {d.latestAnswers[0]?.slice(0, 90) ?? ''}
                </Typography>
              ))}
              <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={seedFromInterview}>
                Seed design session from interview
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  const rec = session.engine.recommendation;
  const current = session.versions.current!;
  const svg = session.facet ? svgWrapper(session.facet, 560) : null;
  const isApproved = current.status === 'approved';

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {session.blockers.length > 0 && (
        <Alert severity="warning">
          <b>Approval blocked:</b> {session.blockers.join(' · ')}
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  {session.state.skiType} · {session.state.length} cm
                </Typography>
                <Chip
                  label={`v${current.version} · ${current.status}`}
                  color={isApproved ? 'success' : 'default'}
                />
              </Stack>
              {current.precedentRef && (
                <Typography variant="caption" color="text.secondary">
                  Precedent: {current.precedentRef.serial ? `serial ${current.precedentRef.serial}` : `orderForm ${current.precedentRef.orderFormId}`}
                </Typography>
              )}
              {svg && (
                <Paper variant="outlined" sx={{ mt: 1, p: 1, overflow: 'auto' }}>
                  <div dangerouslySetInnerHTML={{ __html: svg }} />
                </Paper>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Plan view from the same arcs Ullr RenderSki consumes (1:1 parity).
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <TextField
                  label="Real order ID (dxfSki)" size="small" sx={{ width: 180 }}
                  value={dxfOrderId} onChange={(e) => setDxfOrderId(e.target.value)}
                  placeholder="e.g. 28694" disabled={isApproved}
                />
                <Button
                  size="small" variant="outlined" disabled={isApproved}
                  onClick={() => { const n = Number(dxfOrderId); if (Number.isFinite(n) && n > 0) loadDxfSki(n); else setError('Order ID must be a positive number.'); }}
                >
                  Load production outline
                </Button>
              </Stack>
              {dxfArcs && (() => {
                const wireFacet = buildRenderFacetFromWire({
                  wireArcs: dxfArcs,
                  key: { tailX: 0, waistX: 890, noseX: 1775, tailWidthMm: 118, waistWidthMm: 94, noseWidthMm: 132.4 },
                  profile: {
                    noseHeightMm: 40, noseLengthToContactMm: 120, noseRadiusMm: 350,
                    tailHeightMm: 30, tailLengthToContactMm: 100, tailRadiusMm: 350,
                    runningLengthMm: session.state.length * 10, camberEnum: '2', camberHeightMm: 5,
                    tipRockerName: '', tailRockerName: '',
                    lengthMm: 1780, noseWidthMm: 132.4, waistWidthMm: 94, tailWidthMm: 118,
                    sidecutRadiusM: 17,
                  },
                });
                return wireFacet.notes.length > 0 ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>{wireFacet.notes.join(' · ')}</Alert>
                ) : (
                  <Paper variant="outlined" sx={{ mt: 1, p: 1, overflow: 'auto' }}>
                    <div dangerouslySetInnerHTML={{ __html: svgWrapper(wireFacet, 560) }} />
                  </Paper>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Numbers (authoritative)</Typography>
              <Grid container spacing={1}>
                {OVERRIDABLE.map(({ field, label, step }) => (
                  <Grid size={12} key={field}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        label={label} size="small" fullWidth type="number"
                        inputProps={{ step }}
                        value={editValues[field] ?? String(session.state[field])}
                        onChange={(e) => setEditValues((v) => ({ ...v, [field]: e.target.value }))}
                        disabled={isApproved}
                      />
                      <Button
                        size="small" variant="outlined" disabled={isApproved}
                        onClick={() => applyEdit(field, editValues[field] ?? '')}
                      >
                        Override
                      </Button>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
              <TextField
                label="Reason (required for every override)" size="small" fullWidth
                value={reason} onChange={(e) => setReason(e.target.value)}
                sx={{ mt: 1 }} disabled={isApproved}
              />
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2">Research panel</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label="Research query" size="small" fullWidth
                  value={researchQuery} onChange={(e) => setResearchQuery(e.target.value)}
                  disabled={isApproved}
                />
                <Button size="small" variant="outlined" onClick={lookupResearch} disabled={isApproved}>
                  Look up
                </Button>
              </Stack>
              {researchFinding && (
                <Alert
                  severity={researchFinding.outcome === 'found' ? 'success' : researchFinding.outcome === 'needs-verification' ? 'warning' : 'info'}
                  sx={{ mt: 1 }}
                >
                  <Typography variant="caption" display="block">
                    <b>{researchFinding.outcome}</b> — {researchFinding.rationale}
                  </Typography>
                  {Object.keys(researchFinding.suggestions).length === 0 && (
                    <Typography variant="caption" display="block">No field suggestions to apply.</Typography>
                  )}
                  {Object.entries(researchFinding.suggestions).map(([field, value]) => (
                    <Stack key={field} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <Typography variant="caption">{field}: {String(value)}</Typography>
                      <Button size="small" variant="outlined" onClick={() => applyResearchField(field as ResearchField)}>
                        Apply
                      </Button>
                    </Stack>
                  ))}
                </Alert>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2">Validity</Typography>
              {rec.validity.notes.map((n, i) => (
                <Typography key={i} variant="caption" display="block">• {n}</Typography>
              ))}
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained" color="success" disabled={isApproved || session.blockers.length > 0}
                  onClick={approve}
                >
                  {isApproved ? `Approved by ${current.approvedBy}` : 'Approve version'}
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {session.versions.versions.length} version(s) · append-only · every override carries author + reason + previous value
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}